import React, {forwardRef, useCallback, useImperativeHandle, useState} from "react";
import styled from "@emotion/styled";
import {AlertProps, Alert as BPAlert, Icon} from "@blueprintjs/core";

export interface IAlertRef {
  open: () => void;
  close: () => void;
}

export interface IAlertProps extends Omit<AlertProps, "isOpen"> {
  title?: React.ReactNode;
  message?: React.ReactNode;
  clearState?: () => void;
}

const Alert = forwardRef<IAlertRef, IAlertProps>((props, ref) => {
  const {title, message, clearState, onConfirm, onCancel, ...restProps} = props;
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    clearState?.();
  }, [clearState]);

  const handleCancel = useCallback(() => {
    handleClose();
    onCancel?.();
  }, [handleClose, onCancel]);

  const handleConfirm = useCallback(() => {
    handleClose();
    onConfirm?.();
  }, [handleClose, onConfirm]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose
  }));

  return (
    <StyledAlert
      {...restProps}
      icon={undefined}
      isOpen={isOpen}
      cancelButtonText={onConfirm ? "Cancel" : undefined}
      confirmButtonText={onConfirm ? "Yes" : undefined}
      onClose={handleClose}
      onCancel={handleCancel}
      onConfirm={onConfirm ? handleConfirm : handleCancel}
      intent={onConfirm ? "primary" : "none"}
    >
      <Container>
        <Title>
          <Icon icon={props.icon} />
          {title}
        </Title>
        <Message>{message}</Message>
      </Container>
    </StyledAlert>
  );
});
Alert.displayName = "Alert";

export default Alert;

const StyledAlert = styled(BPAlert)`
  font-family: "Roboto", sans-serif;
  max-width: 50vw;
  width: fit-content;
  padding: 0;
  margin: 0;

  .bp5-alert-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    margin: 0;

    .bp5-button {
      min-width: 50px;
      border-radius: 8px;
      padding: 4px 12px;
      line-height: normal;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Message = styled.div`
  font-size: 14px;
  padding: 0 20px;
`;
